import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Box, Grid, InputAdornment, MenuItem, Select, Switch, TextField, Tooltip } from '@mui/material';
import { Clear, Download, Edit, HighlightOff, IndeterminateCheckBox, MoreVert, Save, Visibility } from '@mui/icons-material';
import { Modal, CircularProgress, Typography, Button } from '@mui/material';
import { defaultMapConfig, useUser } from '../../store/User';
import { apiUrl } from '../../api';
import { terraBaseOptions } from '../Util';
import './index.css';

import { multiPolygon, polygon, featureCollection } from '@turf/helpers';
import turfIntersect from '@turf/intersect';
import turfUnion from '@turf/union';
import { flatten } from '@turf/turf';


export const AOIContainer = () => {
    const { aoi, aois, imagery, location, mapConfig, terraBase, terraBases, tacticalTerraBase, user, worldTerraBase, 
                setAOI, setAOIs,setMapConfig, setShowLogin, setTerraBase, setUser } = useUser();

    const [edit, setEdit] = useState(false);
    const [nameError, showNameError] = useState(false);
    const [nameErrorText, setNameErrorText] = useState("");
    const [coordError, showCoordError] = useState(false);
    const [coordErrorText, setCoordErrorText] = useState("");
    const [showPoints, setShowPoints] = useState(false);
    const [terraBaseOption, setTerraBaseOption] = useState(terraBaseOptions[0]);          
    const [isDownloading, setIsDownloading] = useState(false);    

    const handleClearAOI = () => {
        setAOI(null);
        setTerraBase(null);
    };

    const saveAOI = async () => {
        const extension = aoi.id ? "update" : "create";
        const response = await fetch(`${apiUrl}/aois/${extension}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('terra-token')}`
          },
          body: JSON.stringify(aoi),
        });

        const result = await response.json();

        if (response.ok) {
            setAOIs(result);
            setAOI(result.filter((aois) => aois.name === aoi.name)[0]);
        } else if(response.status === 401) {
            setUser(null);
            setShowLogin(true);
        }
    }

    const inspectAOI = async () => {
        if(aoi) {
            // Close out polygon for db filter
            let inspectObj = {bounds: {...aoi.bounds}};
            if (inspectObj.bounds.coordinates && inspectObj.bounds.coordinates[0].length > 2) {
                inspectObj.bounds.coordinates[0].push(inspectObj.bounds.coordinates[0][0]); // Close the ring
            }
            
            let diff = false;
            for(let i = 0; i < defaultMapConfig.altitudeRange.length; i++) {
                if (defaultMapConfig.altitudeRange[i] !== mapConfig.altitudeRange[i]) {
                    diff = true;
                    break;
                }
            }
            if (diff) {
                inspectObj.altitude_range = mapConfig.altitudeRange;
            }

            const terraBaseView = user?.role && user.role.includes("sales") ? terraBaseOption : "all";

            // put role stuff here so you get full terrabase view

            const response = await fetch(`${apiUrl}/terrabases/${terraBaseView}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('terra-token')}`
                },
                body: JSON.stringify(inspectObj),
            });

            const result = await response.json();

            if (response.ok) {
                setTerraBase(result);
                const minAlt = result.layer.features.reduce((min, feature) => Math.min(min, Math.round(feature.properties.min_alt * 10) / 10), Infinity);
                const maxAlt = result.layer.features.reduce((max, feature) => Math.max(max, Math.round(feature.properties.max_alt * 10) / 10), -Infinity);
                setAOI({
                    ...aoi, 
                    minAlt: minAlt !== Infinity ? minAlt : null,
                    maxAlt: maxAlt !== -Infinity ? maxAlt : null
                });

                setMapConfig({...mapConfig, tactical: false, world: false});
            } else if(response.status === 401) {
                setUser(null);
                setShowLogin(true);
            }
        }
    }

    // Function to union features specifically restricted to a dynamic bounding box
    const getCoverage = () => {
        if(aoi) {
            let collection = [];
            let minAlt = null;
            let maxAlt = null;

            const boundsData = terraBaseOption === "tactical" ? tacticalTerraBase : worldTerraBase;

            for (let box of aoi.bounds.coordinates) {
                const boundsPolygon = polygon(box);

                const boxMin = boundsData.layer.features.reduce((min, feature) => Math.min(min, Math.round(feature.properties.min_alt * 10) / 10), Infinity);
                const boxMax = boundsData.layer.features.reduce((max, feature) => Math.max(max, Math.round(feature.properties.max_alt * 10) / 10), -Infinity);
            
                const clippedFeatures = boundsData?.layer?.features
                    .map(feat => {
                    const pairCollection = featureCollection([boundsPolygon, feat]);
                    const intersected = turfIntersect(pairCollection);
                    if (intersected) {
                        intersected.properties = { 
                            ...feat.properties
                        };
                    }
                    return intersected;
                }).filter(feat => feat !== null);

                if (minAlt === null || boxMin < minAlt) minAlt = boxMin;
                if (maxAlt === null || boxMax > maxAlt) maxAlt = boxMax;


                if (clippedFeatures.length === 0) return null;

                collection = collection.concat(clippedFeatures);
            }

                // 4. Step 2: Create a unified feature collection of your clipped results
                // const clippedCollection = featureCollection(clippedFeatures);

                // Need to set AOI so it shows up.
            setAOI({
                ...aoi, 
                minAlt: minAlt,
                maxAlt: maxAlt
            });

            setTerraBase({layer: featureCollection(collection)});
        }
            
    }

    const downloadAOI = async() => {
        setIsDownloading(true);
        try {
            let downAOI = {...aoi};
            if (downAOI.bounds.coordinates && downAOI.bounds.coordinates[0].length > 2) {
                downAOI.bounds.coordinates[0].push(downAOI.bounds.coordinates[0][0]); // Close the ring
            }

            const response = await fetch(`${apiUrl}/terrabase/download`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('terra-token')}`
                },
                body: JSON.stringify(downAOI),
            });
            
            if (!response.ok) throw new Error('Download failed');

            const blob = await response.blob();

            const url = window.URL.createObjectURL(blob);
            
            const a = document.createElement('a');
            a.href = url;
            a.download = `${aoi?.name ? aoi.name : 'TerraBase'}.zip`; // Suggest the filename
            document.body.appendChild(a);
            a.click();
            
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error downloading job:", error);
        } finally {
            setIsDownloading(false);
        }
    }

    const downloadContainer = () => {
        return (
            <>
                { user?.role && user.role.includes("sales") ?
                    <Select
                        labelId={`job-fov-label`}
                        label="TERRABASE"
                        variant="outlined"
                        size="small"
                        value={terraBaseOption}
                        onChange={(e) => setTerraBaseOption(e.target.value)}
                        sx={{ width: '100%'}}
                    >
                        {terraBaseOptions.map((option) => (
                            <MenuItem key={`terra-base-option-${option}`} value={option}>
                                {option.toUpperCase()}
                            </MenuItem>
                        ))}
                    </Select>
                    :
                    <Tooltip title="Edit AOI" slotProps={{ popper: { sx: { zIndex: 2000 }}}}>
                        <Edit sx={{ color: 'white', mr: 1 }} className='aoi-actions' onClick={() => setMapConfig({...mapConfig, drawPolygons: !mapConfig.drawPolygons})} />
                    </Tooltip>
                }
                <Tooltip title="Inspect TerraBase" slotProps={{ popper: { sx: { zIndex: 2000 }}}}>
                    <Visibility sx={{ color: 'white', mr: 1 }} className='aoi-actions' onClick={() => getCoverage()} />
                </Tooltip>
                <Tooltip title="Download TerraBase" slotProps={{ popper: { sx: { zIndex: 2000 }}}}>
                    <Download sx={{ color: 'white', mr: 1 }} className='aoi-actions' onClick={() => downloadAOI()} />
                </Tooltip>
                <Tooltip title="Clear" slotProps={{ popper: { sx: { zIndex: 2000 }}}}>
                    <Clear sx={{ color: 'white' }} className='aoi-actions' onClick={handleClearAOI} />
                </Tooltip>
            </>
        );
    }

    const handleAOINameChange = (e) => {
        const newName = e.target.value;
        const nameExists = aois.some(existingAOI => existingAOI.name === newName && existingAOI.id !== aoi.id);

        if (nameExists || newName.trim() === "") {
            showNameError(true);
            setNameErrorText("AOI name must be unique");
        } else {
            showNameError(false);
            setNameErrorText("");
        }

        setAOI({ ...aoi, name: newName });
    }

    const editAOIContainer = () => {
        return (
            <Grid container spacing={2}>
                <Grid size={12} sx={{ alignItems: "center",  display: 'flex' }}>
                    <TextField 
                        label="AOI Name" 
                        sx={{ width: 270 }} 
                        variant="outlined" 
                        size="small"
                        value={ aoi?.name || '' }
                        onMouseDown={(e) => e.stopPropagation()}
                        onChange={(e) => handleAOINameChange(e)}
                        error={nameError}
                        helperText={nameError ? nameErrorText : ""}

                        slotProps={{
                        input: {
                            endAdornment: (
                            <InputAdornment position="end">
                                <Tooltip title="Save AOI" slotProps={{ popper: { sx: { zIndex: 2000 }}}}>
                                <Save sx={{ color: '#4c4c4c', ml: 1 }} 
                                    className={ nameError || coordError ? '' : 'save-aoi' } 
                                    onClick={() => saveAOI()}/>
                                </Tooltip>
                            </InputAdornment>
                            ),
                        },
                        }}
                    />
                    <Tooltip title="Show Points" slotProps={{ popper: { sx: { zIndex: 2000 }}}}>
                        <MoreVert sx={{ color: '#fff', cursor: 'pointer' }} onClick={() => setShowPoints(!showPoints)}/>
                    </Tooltip>
                    <Tooltip title="Close Edit" slotProps={{ popper: { sx: { zIndex: 2000 }}}}>
                        <Clear sx={{ color: 'white' }} className='aoi-actions' onClick={() => setMapConfig({...mapConfig, drawPolygons: false})} />
                    </Tooltip>
                </Grid>
                
                { showPoints &&
                    <Grid className="aoi-list" container size={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                    { aoi?.bounds?.coordinates && aoi.bounds.coordinates[0].length > 0 &&
                        aoi.bounds.coordinates[0].map((point, index) => (
                            <Grid  size={12} key={`point-${index}`} container spacing={1} sx={{ alignItems: "center"}}>
                                <Grid size={5}>
                                    <TextField 
                                        label={`Point ${index + 1} Latitude`} 
                                        sx={{ width: '100%' }} 
                                        variant="outlined" 
                                        size="small"
                                        value={`${point[1]}`}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onChange={(e) => {
                                            const lat = parseFloat(e.target.value);
                                            if(!isNaN(lat)) {
                                                const newPoints = [...aoi.bounds.coordinates[0]];
                                                newPoints[index] = [newPoints[index][0], lat];
                                                setAOI({...aoi, bounds: {...aoi.bounds, coordinates: [newPoints]}});
                                                showCoordError(false);
                                            } else {
                                            showCoordError(true);
                                            }
                                        }}

                                        error={coordError}
                                        helperText={coordError ? coordErrorText : ""}
                                    />
                                </Grid>
                                <Grid size={5}>
                                    <TextField 
                                    label={`Point ${index + 1} Longitude`} 
                                    sx={{ width: '100%' }} 
                                    variant="outlined" 
                                    size="small"
                                    value={`${point[0]}`}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                        const lng = parseFloat(e.target.value);
                                        if(!isNaN(lng)) {
                                            const newPoints = [...aoi.bounds.coordinates[0]];
                                            newPoints[index] = [lng, newPoints[index][1]];
                                            setAOI({...aoi, bounds: {...aoi.bounds, coordinates: [newPoints]}});
                                            showCoordError(false);
                                        } else {
                                            showCoordError(true);
                                        }
                                    }}
                                    error={coordError}
                                    helperText={coordError ? coordErrorText : ""}
                                    />
                                </Grid>
                                <Grid size={2}  sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <Tooltip title="Remove Point" slotProps={{ popper: { sx: { zIndex: 2000 }}}}>
                                        <HighlightOff sx={{ color: 'white', cursor: 'pointer', '&:hover': { color: '#ff0000 !important'}, }} onClick={() => {
                                            const newPoints = [...aoi.bounds.coordinates[0]];
                                            newPoints.splice(index, 1);
                                            setAOI({...aoi, bounds: {...aoi.bounds, coordinates: [newPoints]}});
                                        }}/>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                        ))
                    }                     
    {/* { aoi?.mgrs && Object.keys(aoi.mgrs).length > 0 &&
                        Object.keys(aoi.mgrs).map((mgrsGrid, index) => (
                            <Grid size={12} key={`${mgrsGrid}-${index}`} container spacing={1} sx={{ alignItems: "center"}}>
                                <Grid size={10}>
                                    {mgrsGrid}
                                </Grid>
                                <Grid size={2}  sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <Tooltip title="Remove Point">
                                    <RemoveCircle sx={{ color: 'white', cursor: 'pointer', '&:hover': { color: '#ff0000 !important'}, }} onClick={() => {
                                        const newGrids = {...aoi.mgrs};
                                        delete newGrids[mgrsGrid];
                                        setAOI({...aoi, mgrs: newGrids});
                                    }}/>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                        ))
                    } */}
                    </Grid>   
                }
            </Grid>
        );
    };

    // Style object for a compact dark modal centered on screen
    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 360,                  // Compact width
        bgcolor: '#121212',           // Dark / Black background
        color: '#ffffff',             // White text
        borderRadius: 3,              // Rounded corners
        boxShadow: 24,
        p: 3,                         // Compact padding
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',         // Center items horizontally
        justifyContent: 'center',     // Center items vertically
        textAlign: 'center',          // Center-align text strings
        outline: 'none',              // Remove default focus border
    };

    return (
        <>

        {/* MUI Modal with Spinner Box */}
        <Modal
                open={isDownloading}
                disableEscapeKeyDown
                aria-labelledby="download-modal-title"
            >
                <Box sx={modalStyle}>
                    <CircularProgress 
                        size={36} 
                        sx={{ color: '#1aa34a' }} // White spinner to match dark theme
                    />
                    <Typography 
                        id="download-modal-title" 
                        variant="subtitle1" 
                        sx={{ mt: 2, fontWeight: 600, color: '#ffffff', fontSize: 16 }}
                    >
                        Preparing TerraBase Download
                    </Typography>
                    <Typography 
                        variant="caption" 
                        sx={{ mt: 0.5, color: '#a0a0a0', fontSize: 14 }}
                    >
                        Please wait a moment, download will begin automatically...
                    </Typography>
                </Box>
            </Modal>


        <Grid container spacing={2} alignItems="center">



            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                { (mapConfig.drawPolygons && (!user?.role || !user.role.includes("sales"))) ? 
                    editAOIContainer() 
                : 
                    <>
                        <div style={{ cursor: 'pointer', marginRight: '10px' }}>
                            {aoi.name || `AOI`}
                        </div>
                        { downloadContainer() }
                    </>
                }
            </Box>
            { aoi?.minAlt != undefined && aoi?.maxAlt != undefined &&
                            <Grid id="aoi-height" container size={12} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Grid size={6}>
                                    Min Alt: {aoi.minAlt}
                                    </Grid>
                                <Grid size={6}>
                                    Max Alt: {aoi.maxAlt}
                                </Grid>
                            </Grid>
                        }
        </Grid>

        </>
    )
    
}